import React from 'react';
import usePlayerStore from '../stores/playerStore';
import useEventStore from '../stores/eventStore';
import useUserStore from '../stores/userStore';
import {
  buildOccurrenceId,
  hasQualifiedListenProgress,
  isCanonicalRecordingId,
  recordQualifiedListen,
} from '../services/listeningStats';
import { eligibleParticipants } from '../vibe/aggregate';

/**
 * Records at most one qualified listen per playback occurrence for the
 * signed-in user when progress crosses min(50% duration, 120s).
 */
export default function useQualifiedListenTracker({ enabled = true } = {}) {
  const user = useUserStore((state) => state.user);
  const currentSong = usePlayerStore((state) => state.currentSong);
  const currentTime = usePlayerStore((state) => state.currentTime);
  const duration = usePlayerStore((state) => state.duration);
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const eventData = useEventStore((state) => state.eventData);
  const presenceByUid = useEventStore((state) => state.presenceByUid);
  const countedRef = React.useRef(new Set());

  React.useEffect(() => {
    countedRef.current = new Set();
  }, [user?.uid]);

  React.useEffect(() => {
    if (!enabled || !user?.uid || !isPlaying) return;
    const recordingId = currentSong?.recordingId;
    if (!isCanonicalRecordingId(recordingId)) return;

    const occurrenceId = buildOccurrenceId({
      queueItemId: currentSong?.queueItemId,
      recordingId,
      startedAt: currentSong?.addedAt || currentSong?.startedAt,
    });
    const dedupeKey = `${user.uid}:${occurrenceId}`;
    if (countedRef.current.has(dedupeKey)) return;

    if (!hasQualifiedListenProgress({
      currentTimeSeconds: currentTime,
      durationSeconds: duration,
    })) {
      return;
    }

    countedRef.current.add(dedupeKey);
    const eligible = eligibleParticipants(
      eventData?.participants || [],
      presenceByUid || {},
    );
    const mode = eligible.length > 1 ? 'group' : 'solo';

    recordQualifiedListen({
      uid: user.uid,
      recordingId,
      occurrenceId,
      mode,
      title: currentSong?.title || null,
      artist: currentSong?.artist || null,
    }).then((result) => {
      if (!result?.ok || result.counted === false) {
        // Keep the local guard so we do not hammer retries on already-counted.
      }
    });
  }, [
    enabled,
    user?.uid,
    isPlaying,
    currentSong,
    currentTime,
    duration,
    eventData?.participants,
    presenceByUid,
  ]);
}
